import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { MapPin, Calendar, Users, Info, Plane, Music, Tent, Bus, Car, ShieldAlert, Sparkles, MessageCircle, LogOut as Leave, Pencil, Ban } from "lucide-react";
import { tr } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail, friendlyError } from "../lib/api";
import EventChatDialog from "./EventChatDialog";
import EditEventDialog from "./EditEventDialog";

const ICONS = {
  letenky: Plane, zajezd: Bus, vylet: Car, koncert: Music, festival: Tent, sraz: Users,
};

const COUNTRY_LABELS = {
  CZ: "🇨🇿 Česko", SK: "🇸🇰 Slovensko", FR: "🇫🇷 Francie", GR: "🇬🇷 Řecko",
  IT: "🇮🇹 Itálie", ES: "🇪🇸 Španělsko", PT: "🇵🇹 Portugalsko", HR: "🇭🇷 Chorvatsko",
  AT: "🇦🇹 Rakousko", DE: "🇩🇪 Německo", NL: "🇳🇱 Nizozemsko", GB: "🇬🇧 UK",
  PL: "🇵🇱 Polsko", HU: "🇭🇺 Maďarsko", TR: "🇹🇷 Turecko", US: "🇺🇸 USA",
  TH: "🇹🇭 Thajsko", MX: "🇲🇽 Mexiko", MA: "🇲🇦 Maroko", EG: "🇪🇬 Egypt",
  JP: "🇯🇵 Japonsko",
};

function formatPrice(czk, lang) {
  if (!czk || czk === 0) return tr(lang, "card.free");
  return new Intl.NumberFormat(lang === "en" ? "en-GB" : "cs-CZ", { style: "currency", currency: "CZK", maximumFractionDigits: 0 }).format(czk);
}

function countryDisplay(ev) {
  if (ev.country === "OTHER") return `🌍 ${ev.country_custom || "Jiná země"}`;
  return COUNTRY_LABELS[ev.country] || ev.country;
}

function ageDisplay(ev) {
  if (!ev.age_min && !ev.age_max) return "";
  if (ev.age_min && ev.age_max) return `${ev.age_min}–${ev.age_max} let`;
  if (ev.age_min) return `od ${ev.age_min} let`;
  return `do ${ev.age_max} let`;
}

export default function EventDetailDialog({ event: incoming, lang, open, onClose, onChanged }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [event, setEvent] = useState(incoming);
  const [busy, setBusy] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  useEffect(() => { setEvent(incoming); }, [incoming]);

  if (!event) return null;
  const Icon = ICONS[event.category] || Users;

  const isJoined = !!user && (event.attendees_ids || []).includes(user.id);
  const isOwner = !!user && event.created_by === user.id;
  const isCancelled = event.status === "cancelled";

  const refreshOne = async () => {
    try {
      const { data } = await api.get(`/events/${event.id}`);
      setEvent(data);
      onChanged?.();
    } catch (err) { /* ignore */ }
  };

  const handleJoin = async () => {
    if (!user) { onClose(); navigate("/register"); return; }
    if (!user.is_verified) { onClose(); navigate("/verify"); return; }
    setBusy(true);
    try {
      await api.post(`/events/${event.id}/join`);
      toast.success("Připojeno! Otevři chat a poznej parta.");
      await refreshOne();
      setChatOpen(true);
    } catch (e) {
      toast.error(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  const handleLeave = async () => {
    if (!confirm("Opravdu chceš opustit akci?")) return;
    setBusy(true);
    try {
      await api.post(`/events/${event.id}/leave`);
      toast.success("Opustil/a jsi akci.");
      await refreshOne();
    } catch (e) {
      toast.error(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    if (!confirm("Zrušit akci? Účastníkům se to ukáže v chatu.")) return;
    setBusy(true);
    try {
      await api.post(`/events/${event.id}/cancel`);
      toast.success("Akce byla zrušena.");
      await refreshOne();
      onClose();
    } catch (e) {
      toast.error(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Dialog open={open && !chatOpen && !editOpen} onOpenChange={onClose}>
        <DialogContent
          data-testid="event-detail-dialog"
          className="max-w-lg rounded-2xl p-0 overflow-hidden gap-0 bg-white max-h-[90vh] flex flex-col"
        >
          {event.photo_url && (
            <div className="relative w-full aspect-[16/9] bg-slate-100 overflow-hidden shrink-0">
              <img src={event.photo_url} alt={event.title} className="w-full h-full object-cover" onError={(e) => e.currentTarget.style.display = "none"} />
              {isCancelled && (
                <div className="absolute inset-0 bg-black/60 grid place-items-center">
                  <span className="px-4 py-2 rounded-full bg-red-500 text-white font-extrabold text-sm">Akce zrušena</span>
                </div>
              )}
            </div>
          )}

          <div className="p-6 sm:p-8 flex flex-col gap-4 overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-10 h-10 rounded-xl bg-sky-50 text-sky-600 grid place-items-center">
                  <Icon className="w-5 h-5" />
                </div>
                <span className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500">
                  {tr(lang, `categories.${event.category}`)}
                </span>
                {event.is_hot && (
                  <span className="ml-auto text-xs font-bold px-2.5 py-1 rounded-full bg-amber-100 text-amber-700">🔥 HOT</span>
                )}
              </div>
              <DialogTitle className="font-heading text-2xl sm:text-3xl text-left leading-tight">{event.title}</DialogTitle>
            </DialogHeader>

            <div className="flex flex-col gap-3 text-sm font-medium text-slate-700 mt-2">
              <div className="flex items-center gap-2.5">
                <MapPin className="w-4 h-4 text-sky-500" />
                <span><b className="text-slate-900">Kde:</b> {event.city}, {countryDisplay(event)}</span>
              </div>
              {event.country_note && (
                <div className="pl-6 -mt-1 text-xs text-slate-500">{event.country_note}</div>
              )}
              <div className="flex items-center gap-2.5">
                <Calendar className="w-4 h-4 text-sky-500" />
                <span><b className="text-slate-900">Kdy:</b> {event.date_text || event.date}</span>
              </div>
              {event.capacity > 0 && (
                <div className="flex items-center gap-2.5">
                  <Users className="w-4 h-4 text-sky-500" />
                  <span><b className="text-slate-900">Kapacita:</b> {event.attendees}/{event.capacity} lidí{ageDisplay(event) ? ` · věk ${ageDisplay(event)}` : ""}</span>
                </div>
              )}
              {event.description && (
                <div className="flex items-start gap-2.5 mt-1">
                  <Info className="w-4 h-4 text-sky-500 mt-0.5 shrink-0" />
                  <span className="text-slate-600 leading-relaxed">{event.description}</span>
                </div>
              )}
            </div>

            {event.organizer_note && (
              <div className="mt-2 rounded-2xl bg-slate-50 border border-slate-100 p-4">
                <div className="text-xs uppercase tracking-[0.12em] font-bold text-slate-500 flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> Kdo to vymyslel</div>
                <div className="mt-1.5 text-sm text-slate-700 font-medium leading-relaxed">{event.organizer_note}</div>
                <div className="mt-2 text-xs font-bold text-slate-600">{event.organizer}</div>
              </div>
            )}

            {user && !user.is_verified && !isOwner && (
              <div data-testid="event-detail-verify-notice" className="mt-2 rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm flex items-start gap-2">
                <ShieldAlert className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <span className="text-amber-800 font-medium">Pro připojení je potřeba ověřený profil (foto občanky kvůli bezpečnosti).</span>
              </div>
            )}

            <div className="ticket-divider my-3" style={{ marginLeft: '-24px', marginRight: '-24px' }} />

            <div className="flex flex-col gap-2.5">
              <div className="flex items-end justify-between gap-4">
                <div className="flex flex-col">
                  <span className="text-xs uppercase tracking-[0.12em] font-bold text-slate-400">
                    {event.perks || event.price_label || "Cena"}
                  </span>
                  <span className="font-heading font-extrabold text-2xl text-slate-900 mt-1">
                    {formatPrice(event.price_czk, lang)}
                  </span>
                </div>

                {isCancelled ? (
                  <span className="text-sm font-bold text-red-600">Akce zrušena</span>
                ) : isOwner ? (
                  <button
                    data-testid="event-detail-chat-btn"
                    onClick={() => setChatOpen(true)}
                    className="px-5 py-3 rounded-full bg-sky-500 hover:bg-sky-600 text-white font-bold text-sm transition-all active:scale-95 shadow-[0_10px_30px_-8px_rgba(14,165,233,0.55)] flex items-center gap-2"
                  >
                    <MessageCircle className="w-4 h-4" /> Otevřít chat
                  </button>
                ) : isJoined ? (
                  <button
                    data-testid="event-detail-chat-btn"
                    onClick={() => setChatOpen(true)}
                    className="px-5 py-3 rounded-full bg-sky-500 hover:bg-sky-600 text-white font-bold text-sm transition-all active:scale-95 shadow-[0_10px_30px_-8px_rgba(14,165,233,0.55)] flex items-center gap-2"
                  >
                    <MessageCircle className="w-4 h-4" /> Otevřít chat
                  </button>
                ) : (
                  <button
                    data-testid="event-detail-reserve-btn"
                    onClick={handleJoin}
                    disabled={busy}
                    className="px-6 py-3 rounded-full bg-sky-500 hover:bg-sky-600 disabled:opacity-60 text-white font-bold text-sm transition-all active:scale-95 shadow-[0_10px_30px_-8px_rgba(14,165,233,0.55)]"
                  >
                    {!user ? "Zaregistruj se a připoj se" : !user.is_verified ? "Ověř občanku" : "Připojit se"}
                  </button>
                )}
              </div>

              {/* Secondary actions */}
              <div className="flex flex-wrap gap-2 mt-2">
                {isJoined && !isOwner && !isCancelled && (
                  <button
                    data-testid="event-detail-leave-btn"
                    onClick={handleLeave}
                    disabled={busy}
                    className="text-xs text-slate-500 hover:text-red-600 font-bold flex items-center gap-1 underline underline-offset-4"
                  >
                    <Leave className="w-3 h-3" /> Opustit akci
                  </button>
                )}
                {isOwner && !isCancelled && (
                  <>
                    <button
                      data-testid="event-detail-edit-btn"
                      onClick={() => setEditOpen(true)}
                      className="text-xs text-sky-600 hover:text-sky-700 font-bold flex items-center gap-1 underline underline-offset-4"
                    >
                      <Pencil className="w-3 h-3" /> Upravit
                    </button>
                    <button
                      data-testid="event-detail-cancel-btn"
                      onClick={handleCancel}
                      className="text-xs text-red-600 hover:text-red-700 font-bold flex items-center gap-1 underline underline-offset-4"
                    >
                      <Ban className="w-3 h-3" /> Zrušit akci
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <EventChatDialog event={event} open={chatOpen} onClose={() => setChatOpen(false)} />
      <EditEventDialog event={event} open={editOpen} onClose={() => setEditOpen(false)} onSaved={refreshOne} />
    </>
  );
}
